import { useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLocationDot,
  faPhone,
  faEnvelope,
  faArrowDown,
  faCopy,
  faCheck,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faGithub, faFacebook } from '@fortawesome/free-brands-svg-icons';

import resumePdf from '../../assets/Darshan_Lal_CV.pdf';
import { siteConfig } from '../../config/site';
import useMagnetic from '../../hooks/useMagnetic';
import Reveal from '../ui/Reveal';
import SpotlightCard from '../ui/SpotlightCard';

import '../../styles/Contact/Contact.scss';

const Contact = () => {
  const { contactEmail, phoneDisplay, phoneTel, locationLine, social, resumeDownloadFilename } = siteConfig;
  const [copied, setCopied] = useState(false);
  const ctaRef = useMagnetic(0.3);

  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 2200);
    return () => clearTimeout(id);
  }, [copied]);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(contactEmail);
      setCopied(true);
    } catch {
      window.location.href = `mailto:${contactEmail}`;
    }
  };

  const socials = [
    { href: social.linkedin, icon: faLinkedin, label: 'LinkedIn' },
    { href: social.github, icon: faGithub, label: 'GitHub' },
    { href: social.facebook, icon: faFacebook, label: 'Facebook' },
  ];

  return (
    <section className="section contact" id="contact" aria-labelledby="contact-title">
      <div className="container">
        <Reveal className="contact__panel" variant="zoom">
          <div className="contact__orb contact__orb--1" aria-hidden="true" />
          <div className="contact__orb contact__orb--2" aria-hidden="true" />

          <p className="contact__eyebrow">
            <span>06</span> — Contact
          </p>
          <h2 className="contact__title" id="contact-title">
            Have a project in mind?
            <br />
            <span className="gradient-text">Let&apos;s build something remarkable.</span>
          </h2>
          <p className="contact__lead">
            Hiring a Frontend Developer, Backend Developer, Full Stack or Forward Deployed Engineer? I&apos;m available
            immediately and open to relocation. My inbox is always open, and I usually reply within a day.
          </p>

          <div className="contact__cta">
            <a ref={ctaRef} className="btn btn--primary contact__big-btn" href={`mailto:${contactEmail}`}>
              <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" /> Say hello{' '}
              <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </a>
            <button type="button" className="btn btn--ghost" onClick={copyEmail}>
              <FontAwesomeIcon icon={copied ? faCheck : faCopy} aria-hidden="true" />
              {copied ? 'Copied!' : 'Copy email'}
            </button>
            <a className="btn btn--ghost" href={resumePdf} download={resumeDownloadFilename}>
              Résumé <FontAwesomeIcon icon={faArrowDown} aria-hidden="true" />
            </a>
          </div>
          <p className="sr-only" aria-live="polite">
            {copied ? 'Email address copied to clipboard' : ''}
          </p>
        </Reveal>

        <div className="contact__grid">
          <Reveal delay={0}>
            <SpotlightCard as="a" href={`mailto:${contactEmail}`} className="contact__card">
              <span className="contact__icon" aria-hidden="true">
                <FontAwesomeIcon icon={faEnvelope} />
              </span>
              <span className="contact__label">Email</span>
              <span className="contact__value">{contactEmail}</span>
            </SpotlightCard>
          </Reveal>
          <Reveal delay={100}>
            <SpotlightCard as="a" href={`tel:${phoneTel}`} className="contact__card">
              <span className="contact__icon" aria-hidden="true">
                <FontAwesomeIcon icon={faPhone} />
              </span>
              <span className="contact__label">Phone</span>
              <span className="contact__value">{phoneDisplay}</span>
            </SpotlightCard>
          </Reveal>
          <Reveal delay={200}>
            <SpotlightCard className="contact__card">
              <span className="contact__icon" aria-hidden="true">
                <FontAwesomeIcon icon={faLocationDot} />
              </span>
              <span className="contact__label">Location</span>
              <span className="contact__value">{locationLine}</span>
            </SpotlightCard>
          </Reveal>
        </div>

        <Reveal className="contact__social" delay={100}>
          {socials.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}>
              <FontAwesomeIcon icon={s.icon} />
              <span>{s.label}</span>
            </a>
          ))}
        </Reveal>
      </div>

      <div className={`contact__toast${copied ? ' is-shown' : ''}`} aria-hidden="true">
        <FontAwesomeIcon icon={faCheck} /> Email copied to clipboard
      </div>
    </section>
  );
};

export default Contact;
